import { option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { DEFAULT_LOCALE, IntlFormat } from '../constants';
import { getValidIntlLocale } from '../locale';
import { LocaleType } from '../locale.types';
import { IntlDateFormats, IntlTimeFormats } from './constants';
import {
  DateFormat,
  DateFormatConfig,
  IntlDateTimeFormatOptions,
  TimeFormat,
  TimeFormatConfig,
  TimezoneOffsetFormat,
} from './types';

export function createTimeFormatConfig(
  timeStyle?: TimeFormat,
  timeZone?: string,
): TimeFormatConfig {
  const tzConfig = timeZone ? { timeZone, timeZoneName: TimezoneOffsetFormat.Short } : {};

  return pipe(
    timeStyle,
    option.fromNullable,
    option.flatMap((fmt) => record.lookup(fmt)(IntlTimeFormats)),
    option.getOrElse<TimeFormatConfig>(() => ({})),
    (v) => ({
      ...v,
      ...tzConfig,
    }),
  );
}

export function createDateFormatConfig(dateStyle?: DateFormat): DateFormatConfig {
  return pipe(
    record.lookup(dateStyle ?? DateFormat.Short)(IntlDateFormats),
    option.getOrElse<DateFormatConfig>(() => ({})),
  );
}

export function createDateTimeFormat(
  locale: LocaleType = DEFAULT_LOCALE,
  config?: IntlDateTimeFormatOptions,
) {
  return new Intl.DateTimeFormat(getValidIntlLocale(locale, IntlFormat.DateTimeFormat), config);
}

/**
 * createRelativeTimeFormat
 *
 * Formats a value as a string representation of relative value
 *
 * @param locale A single locale or array of locales to use in finding the best match
 *
 * @example en-US, -5, 'day' => 5 days ago
 */
export const createRelativeTimeFormat = (locale: LocaleType = DEFAULT_LOCALE) => {
  const formatLocale = getValidIntlLocale(locale, IntlFormat.RelativeTimeFormat);

  return new Intl.RelativeTimeFormat(formatLocale, {
    numeric: 'always',
    style: 'long',
  });
};

/**
 * getCompactDateTimeFormat
 *
 * Formats a Date object to provide the full datetime using shortened names
 *
 * @param locale A single locale or array of locales to use in finding the best match
 *
 * @example en-US, 2020-03-17T14:21:56.000 => Tue, Mar 17, 2020, 2:21 PM
 * @deprecated Use createDateTimeFormat
 */
export const getShortFullDateTimeFormat = (locale: LocaleType = DEFAULT_LOCALE) => {
  const formatLocale = getValidIntlLocale(locale, IntlFormat.DateTimeFormat);

  return new Intl.DateTimeFormat(formatLocale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};
